import { inject, Injectable, OnDestroy, Provider } from '@angular/core';
import { DefaultLocale } from '@app/+authenticated/shared/locale/locale.helper';
import { TranslateService } from '@ngx-translate/core';
import {
  INITIAL_CONFIG,
  NgxMaskConfig,
  initialConfig as ngxMaskInitialConfig,
  NgxMaskService,
  provideNgxMask,
} from 'ngx-mask';
import { debounceTime, distinctUntilChanged, map, Subject, takeUntil } from 'rxjs';

const decimalSeparator = (lang: string) => new Intl.NumberFormat(lang, {}).format(1.1).substring(1, 2) as ',' | '.';

const thousandSeparator = (lang: string) => new Intl.NumberFormat(lang, {}).format(1000).substring(1, 2);

export const provideMask = (): Provider[] => [
  {
    provide: INITIAL_CONFIG,
    useFactory: (translate: TranslateService): NgxMaskConfig => ({
      ...ngxMaskInitialConfig,
      validation: false,
      allowNegativeNumbers: false,
      showMaskTyped: false,
      // if we want to support am/pm this would have to be set either depending on lang or settings
      apm: false,
      // use getter to use most recent language since directive will use own instance of the service
      get decimalMarker() {
        return decimalSeparator(translate.getCurrentLang() ?? DefaultLocale);
      },
      get thousandSeparator() {
        return thousandSeparator(translate.getCurrentLang() ?? DefaultLocale);
      },
    }),
    deps: [TranslateService],
  },
  provideNgxMask(),
  MaskConfigService,
];

@Injectable()
export class MaskConfigService implements OnDestroy {
  private translate = inject(TranslateService);
  private maskService = inject(NgxMaskService);

  private destroyed$ = new Subject<void>();

  init() {
    void this.translate.onLangChange
      .pipe(
        // due to language changing multiple times in a row on initialization, we debounce the event
        debounceTime(3000),
        map((event) => event.lang),
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
      )
      .subscribe((lang) => {
        this.maskService.thousandSeparator = thousandSeparator(lang);
        this.maskService.decimalMarker = decimalSeparator(lang);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
